import { useUserWithCompany } from '@app/hooks/useUserWithCompany';
import { AlertDialog, DeadlineBadge, Loading } from '@components/ui';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ReplayIcon from '@mui/icons-material/Replay';
import { Button } from '@mui/material';
import MUIDataTable, { MUIDataTableMeta, MUIDataTableOptions, MUIDataTableProps } from 'mui-datatables';
import { useMemo, useState } from 'react';

import { useNavigation } from '@app/hooks/useNavigation';
import { useDashboard } from '@app/routes/Dashboard/useDashboard';
import { ThreeDotItem, ThreeDots } from '@components/ui/ThreeDots/ThreeDots';
import {
  ApplicationType,
  TagValues,
  UserQuestionnaireApplicationTopic,
  UserTopicApplicationsQuestionnaires,
} from '@dieterApi/user/useUserQuery';

import { getTopicStates } from '@app/utils/getTopicStates';
import { useUserValueAddMutation } from '@dieterApi/user/useUserValueAddMutation';
import { useUserValueDeleteMutation } from '@dieterApi/user/useUserValueDeleteMutation';
import { Link } from 'react-router-dom';
import './requests.sass';

import { RightsRequest, useRightsRequestsQuery } from '@dieterApi/rightsRequest/useRightsRequestsQuery';

interface Props {
  topic: UserTopicApplicationsQuestionnaires;
  topicStates: ReturnType<typeof getTopicStates>;
}

const titleTagMap = {
  'Recht auf Berichtigung': 'RIGHT_TO_RECTIFICATION',
  'Recht auf Einschränkung': 'RIGHT_TO_RESTRICTION',
  'Recht auf Datenübertragbarkeit': 'RIGHT_TO_DATA_PORTABILITY',
  'Recht auf Widerspruch': 'RIGHT_TO_OBJECTION',
  'Recht auf Widerruf der Einwilligung': 'RIGHT_TO_REVOCATION',
  Auskunftsrecht: 'RIGHT_TO_INFORMATION',
  'Recht auf Löschung': 'RIGHT_TO_DELETION',
  'Recht keiner automatischen Entscheidung unterworfen zu werden': 'RIGHT_TO_DENY_AUTOMATED_DECISION',
};

const extensionTag = 'EXTEND_DEADLINE';

// All PersistentState keys written per request index - must stay in sync with
// RIGHTS_REQUEST_KEYS in dieter-server src/utils/rightsRequestDeadline.ts, otherwise
// deleting a request leaves orphaned rows behind.
const requestKeys = [
  'REQUEST_TIME_OF_ENTRY',
  'REQUEST_TYPE_OF_RIGHT',
  'REQUEST_DATE',
  'REQUEST_COMPLETED_AT',
  'REQUEST_OVERDUE',
  'DOUBTS_ABOUT_IDENTITY_EXIST',
];

// Sort urgency: overdue requests first, done and unknown last
const statusOrder: Record<RightsRequest['status'], number> = {
  OVERDUE: 0,
  CRITICAL: 1,
  DUE_SOON: 2,
  OPEN: 3,
  UNKNOWN: 4,
  DONE: 5,
};

export interface RequestTableMeta extends MUIDataTableMeta {
  rowData: [
    string,
    string,
    string,
    keyof typeof titleTagMap,
    UserQuestionnaireApplicationTopic, // the questionnaire of the request
    UserQuestionnaireApplicationTopic, // the questionnaire of the response
    UserQuestionnaireApplicationTopic, // the questionnaire of the deadline extension
    RightsRequest,
  ];
}
export function Requests({ topic, topicStates }: Props) {
  const { user, questionnaires } = useUserWithCompany();
  const { data: rightsRequestsData, loading: rightsRequestsLoading } = useRightsRequestsQuery();
  const dashboard = useDashboard();
  const { setNavigation } = useNavigation();

  const [deleteAlertOpen, setDeleteAlertOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState<MUIDataTableMeta | null>(null);

  const addRequestApp = topic.applications.find((app) => app.type === ApplicationType.ListEntryShort);

  const requestData = useMemo(
    () => extractRequestsData(rightsRequestsData?.getRightsRequests, questionnaires),
    [rightsRequestsData, questionnaires]
  );

  const [deleteUserValue] = useUserValueDeleteMutation();
  const [addUserValue] = useUserValueAddMutation();

  const handleDelete = (tableMeta: MUIDataTableMeta) => {
    const index = tableMeta.rowData?.[0] as string;
    if (!index) return;
    requestKeys.forEach((key) =>
      deleteUserValue({
        variables: { key, index },
        context: { userId: user?.id || '' },
        refetchQueries: ['GetRightsRequests', 'GetDpoTasks'],
        // not every key exists for every request - ignore missing rows
      }).catch(() => undefined)
    );
  };

  const handleAdd = () => {
    addRequestApp && dashboard.create(addRequestApp.id);
  };

  const handleProcess = (tableMeta: RequestTableMeta) => {
    const index = tableMeta.rowData?.[0];
    const typeOfRight = tableMeta.rowData?.[3];
    const contract = tableMeta.rowData?.[5];
    if (!contract) {
      const tag = titleTagMap[typeOfRight];
      const app = topic.applications.find(
        (app) => app.type === ApplicationType.ListContract && app.tags?.map((t) => t.tag).includes(tag as TagValues)
      );
      if (!app) return;
      // onlyIfNotExists: never create a second response letter for the same request,
      // even if the local cache is stale
      dashboard.create(app.id, undefined, true, index, undefined, undefined, undefined, true);
    } else {
      dashboard.open(contract.id);
    }
  };

  const handleExtension = (tableMeta: RequestTableMeta) => {
    const index = tableMeta.rowData?.[0];
    const extension = tableMeta.rowData?.[6];
    if (extension) {
      dashboard.open(extension.id);
      return;
    }
    const app = topic.applications.find(
      (app) => app.type === ApplicationType.ListContract && app.tags?.map((t) => t.tag).includes(extensionTag)
    );
    if (!app) return;
    dashboard.create(app.id, undefined, true, index, undefined, undefined, undefined, true);
  };

  const handleToggleCompleted = (tableMeta: RequestTableMeta) => {
    const index = tableMeta.rowData?.[0];
    const rightsRequest = tableMeta.rowData?.[7];
    if (!index) return;
    if (rightsRequest?.status === 'DONE') {
      deleteUserValue({
        variables: { key: 'REQUEST_COMPLETED_AT', index },
        context: { userId: user?.id || '' },
        refetchQueries: ['GetRightsRequests', 'GetDpoTasks'],
      }).catch(() => undefined);
    } else {
      addUserValue({
        variables: { key: 'REQUEST_COMPLETED_AT', value: new Date().toISOString(), index },
        refetchQueries: ['GetRightsRequests', 'GetDpoTasks'],
      });
    }
  };

  const columns: MUIDataTableProps['columns'] = [
    {
      name: 'index',
      options: {
        display: 'excluded',
      },
    },
    {
      name: 'receivedAt',
      label: 'Eingang',
      options: {
        sort: false,
        customBodyRender: (value) => {
          return value ? new Date(value).toLocaleDateString('de-DE') : '—';
        },
      },
    },
    {
      name: 'requestDate',
      label: 'Erfasst am',
      options: {
        sort: false,
        customBodyRender: (value) => {
          return value ? new Date(value).toLocaleDateString('de-DE') : '—';
        },
      },
    },
    {
      name: 'typeOfRight',
      label: 'Art des Rechts',
      options: {
        sort: false,
      },
    },
    {
      name: 'request',
      options: {
        display: 'excluded',
      },
    },
    {
      name: 'contract',
      options: {
        display: 'excluded',
      },
    },
    {
      name: 'extension',
      options: {
        display: 'excluded',
      },
    },
    {
      name: 'rightsRequest',
      options: {
        display: 'excluded',
      },
    },
    {
      name: 'deadline',
      label: 'Frist',
      options: {
        sort: false,
        customBodyRender: (value, tableMeta) => {
          const rightsRequest = tableMeta.rowData[7] as RightsRequest;
          const contract = tableMeta.rowData[5] as UserQuestionnaireApplicationTopic;
          if (!rightsRequest) return null;
          const showCompletionHint = rightsRequest.status !== 'DONE' && (contract?.progress || 0) >= 1;
          return (
            <div className="flex flex-col gap-1 items-start">
              <DeadlineBadge
                status={rightsRequest.status}
                deadline={rightsRequest.deadline}
                completedAt={rightsRequest.completedAt}
                isExtended={rightsRequest.isExtended}
              />
              {showCompletionHint && (
                <span className="text-xs text-gray-500">Antwortschreiben fertig – Anfrage erledigt?</span>
              )}
            </div>
          );
        },
      },
    },
    {
      name: 'documents',
      label: 'Dokumente',
      options: {
        sort: false,
        customBodyRender: (value, tableMeta) => {
          const request = tableMeta.rowData[4] as UserQuestionnaireApplicationTopic;
          const contract = tableMeta.rowData[5] as UserQuestionnaireApplicationTopic;
          const extension = tableMeta.rowData[6] as UserQuestionnaireApplicationTopic;

          const openDocument = (questionnaire: UserQuestionnaireApplicationTopic) => {
            setNavigation((nav) => {
              nav.documentPaper = {
                modalOpen: true,
                questionnaire,
                index: 0,
              };
            });
          };

          return (
            <ul>
              {request && (
                <li>
                  <Link to="#" onClick={() => openDocument(request)}>
                    Eingangsbestätigung
                  </Link>
                </li>
              )}
              {contract && (
                <li>
                  <Link to="#" onClick={() => openDocument(contract)}>
                    Antwortschreiben
                  </Link>
                </li>
              )}
              {extension && (
                <li>
                  <Link to="#" onClick={() => openDocument(extension)}>
                    Fristverlängerung
                  </Link>
                </li>
              )}
            </ul>
          );
        },
      },
    },
    {
      name: 'actions',
      options: {
        sort: false,
        customHeadRender: () => '',
        customBodyRender: (value: any, tableMeta: MUIDataTableMeta) => {
          const contract = tableMeta.rowData[5] as UserQuestionnaireApplicationTopic;
          const extension = tableMeta.rowData[6] as UserQuestionnaireApplicationTopic;
          const rightsRequest = tableMeta.rowData[7] as RightsRequest;
          const isDone = rightsRequest?.status === 'DONE';
          const threeDotItems: ThreeDotItem[] = [
            {
              label: contract ? 'Antwortschreiben bearbeiten' : 'Antwortschreiben erstellen',
              onClick: () => {
                handleProcess(tableMeta as RequestTableMeta);
              },
              icon: contract ? <EditIcon fontSize="small" /> : <AddIcon fontSize="small" />,
            },
            {
              label: extension ? 'Fristverlängerung bearbeiten' : 'Fristverlängerung beantragen',
              onClick: () => {
                handleExtension(tableMeta as RequestTableMeta);
              },
              icon: extension ? <EditIcon fontSize="small" /> : <AddIcon fontSize="small" />,
            },
            {
              label: isDone ? 'Wieder öffnen' : 'Als erledigt markieren',
              onClick: () => {
                handleToggleCompleted(tableMeta as RequestTableMeta);
              },
              icon: isDone ? <ReplayIcon fontSize="small" /> : <CheckCircleOutlineIcon fontSize="small" />,
            },
            {
              label: 'Anfrage löschen',
              onClick: () => {
                setDeleteRow(tableMeta);
                setDeleteAlertOpen(true);
              },
              icon: <DeleteIcon fontSize="small" />,
            },
          ];

          return <ThreeDots items={threeDotItems} />;
        },
      },
    },
  ];

  const options: MUIDataTableOptions = {
    download: false,
    print: false,
    elevation: 0,
    selectableRows: 'none',
    selectableRowsHeader: false,

    customToolbar: () => (
      <Button variant="outlined" onClick={handleAdd} startIcon={<AddIcon />} disabled={user?.isReadOnly} size="small">
        Hinzufügen
      </Button>
    ),
    search: false,
    filter: false,
    viewColumns: false,
    pagination: false,
    textLabels: {
      selectedRows: {
        text: 'Zeilen ausgewählt',
        delete: 'Löschen',
        deleteAria: 'Ausgewählte Zeilen löschen',
      },
      body: {
        noMatch: 'Keine Einträge vorhanden',
      },
    },
  };

  return (
    <div className="dtRequests">
      {!rightsRequestsLoading || requestData.length > 0 ? (
        <MUIDataTable columns={columns} data={requestData} title="Deine Anfragen" options={options} />
      ) : (
        <Loading />
      )}
      <AlertDialog
        open={deleteAlertOpen}
        setOpen={setDeleteAlertOpen}
        message={
          <>
            Möchtest du die Anfrage zum <b>{deleteRow?.rowData[3]}</b> vom{' '}
            <b>{deleteRow?.rowData[1] ? new Date(deleteRow.rowData[1]).toLocaleDateString('de-DE') : 'unbekannt'}</b>{' '}
            wirklich löschen?
          </>
        }
        onAccept={() => deleteRow && handleDelete(deleteRow)}
      />
    </div>
  );
}

interface RequestsData {
  index: string;
  receivedAt?: string;
  requestDate?: string;
  typeOfRight: string;
  request?: UserQuestionnaireApplicationTopic;
  contract?: UserQuestionnaireApplicationTopic;
  extension?: UserQuestionnaireApplicationTopic;
  rightsRequest: RightsRequest;
}

const extractRequestsData = (
  rightsRequests: RightsRequest[] | undefined,
  questionnaires: UserQuestionnaireApplicationTopic[] | undefined
): RequestsData[] => {
  if (!rightsRequests) return [];

  const findQuestionnaire = (id?: string) => (id ? questionnaires?.find((q) => q.id === id) : undefined);

  return rightsRequests
    .map((rightsRequest) => ({
      index: rightsRequest.index,
      receivedAt: rightsRequest.receivedAt,
      requestDate: rightsRequest.requestDate,
      typeOfRight: rightsRequest.typeOfRight || '',
      request: findQuestionnaire(rightsRequest.entryQuestionnaireId || rightsRequest.index),
      contract: findQuestionnaire(rightsRequest.contractQuestionnaireId),
      extension: findQuestionnaire(rightsRequest.extensionQuestionnaireId),
      rightsRequest,
    }))
    .sort((a, b) => {
      const orderDiff = statusOrder[a.rightsRequest.status] - statusOrder[b.rightsRequest.status];
      if (orderDiff !== 0) return orderDiff;
      return (a.rightsRequest.daysRemaining ?? 0) - (b.rightsRequest.daysRemaining ?? 0);
    });
};
